import { SelectField } from "@peer-start/ui";
import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { SchoolProfile } from "../../data/school-profile.js";
import { SchoolSetupDocumentField, SchoolSetupField } from "./SchoolSetupFields.js";

export function SchoolSetupSteps({
  currentStep,
  profile,
  patch,
}: {
  currentStep: number;
  profile: SchoolProfile;
  patch: (partial: Partial<SchoolProfile>) => void;
}) {
  const { t } = useTranslation();
  if (currentStep === 0) {
    return (
      <section aria-labelledby="school-profile-title" className="space-y-6">
        <div>
          <h1 className="text-3xl font-medium tracking-tight" id="school-profile-title">
            {t("school.profile.title")}
          </h1>
          <p className="mt-2 text-muted-foreground">{t("school.profile.description")}</p>
        </div>
        <SchoolSetupField
          controlId="school.profile.name"
          label={t("school.profile.schoolName")}
          onChange={(schoolName) => patch({ schoolName })}
          value={profile.schoolName}
        />
        <SchoolSetupField
          controlId="school.profile.foundation"
          label={t("school.profile.foundation")}
          onChange={(foundation) => patch({ foundation })}
          value={profile.foundation}
        />
        <SelectField
          controlId="school.profile.region"
          label={t("school.profile.region")}
          onChange={(event) => patch({ region: event.target.value })}
          value={profile.region}
        >
          {["Utrecht", "Noord-Holland", "Zuid-Holland", "Gelderland", "Noord-Brabant"].map((region) => (
            <option key={region} value={region}>
              {region}
            </option>
          ))}
        </SelectField>
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium">{t("school.profile.staff")}</legend>
          {profile.staffRoles.map((entry, index) => (
            <div className="grid grid-cols-[minmax(0,1fr)_7rem_auto] items-end gap-3" key={entry.id}>
              <SelectField
                controlId={`school.profile.staff.${index}.role`}
                label={t("school.profile.role")}
                onChange={(event) =>
                  patch({
                    staffRoles: profile.staffRoles.map((role) =>
                      role.id === entry.id ? { ...role, role: event.target.value } : role,
                    ),
                  })
                }
                value={entry.role}
              >
                {["Leraar", "Onderwijsondersteuner", "Intern begeleider", "Schoolleider", "Coach"].map(
                  (role) => (
                    <option key={role} value={role}>
                      {role === "Coach" ? t("userManagement.roles.mentor") : role}
                    </option>
                  ),
                )}
              </SelectField>
              <SchoolSetupField
                controlId={`school.profile.staff.${index}.amount`}
                label={t("school.profile.amount")}
                onChange={(amount) =>
                  patch({
                    staffRoles: profile.staffRoles.map((role) =>
                      role.id === entry.id ? { ...role, amount: Number(amount) } : role,
                    ),
                  })
                }
                type="number"
                value={String(entry.amount)}
              />
              <button
                aria-label={t("school.profile.removeRole", { role: entry.role })}
                className="grid size-11 place-items-center rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
                data-control-id={`school.profile.staff.${index}.remove`}
                onClick={() =>
                  patch({ staffRoles: profile.staffRoles.filter((role) => role.id !== entry.id) })
                }
                type="button"
              >
                <Trash2 aria-hidden="true" className="size-4" />
              </button>
            </div>
          ))}
          <button
            className="inline-flex items-center gap-2 text-sm font-medium text-link hover:underline focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
            data-control-id="school.profile.staff.add"
            onClick={() =>
              patch({
                staffRoles: [...profile.staffRoles, { id: `role-${Date.now()}`, role: "Leraar", amount: 1 }],
              })
            }
            type="button"
          >
            <Plus aria-hidden="true" className="size-4" />
            {t("school.profile.addRole")}
          </button>
        </fieldset>
      </section>
    );
  }
  if (currentStep === 1) {
    return (
      <section aria-labelledby="school-culture-title" className="space-y-6">
        <div>
          <h1 className="text-3xl font-medium tracking-tight" id="school-culture-title">
            {t("school.culture.title")}
          </h1>
          <p className="mt-2 text-muted-foreground">{t("school.culture.description")}</p>
        </div>
        <SelectField
          controlId="school.culture.educationalType"
          label={t("school.culture.educationalType")}
          onChange={(event) => patch({ educationalType: event.target.value })}
          value={profile.educationalType}
        >
          {["Regulier onderwijs", "Speciaal onderwijs", "Passend onderwijs"].map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </SelectField>
        <SelectField
          controlId="school.culture.organisationType"
          label={t("school.culture.organisationType")}
          onChange={(event) => patch({ organisationType: event.target.value })}
          value={profile.organisationType}
        >
          {["Reguliere school", "Brede school", "Onderwijscommunity"].map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </SelectField>
        <SelectField
          controlId="school.culture.schoolType"
          label={t("school.culture.schoolType")}
          onChange={(event) => patch({ schoolType: event.target.value })}
          value={profile.schoolType}
        >
          {["Basisonderwijs", "Voortgezet onderwijs", "Speciaal basisonderwijs"].map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </SelectField>
        <SchoolSetupField
          controlId="school.culture.specification"
          label={t("school.culture.specification")}
          onChange={(specification) => patch({ specification })}
          value={profile.specification}
        />
      </section>
    );
  }
  return (
    <section aria-labelledby="school-documents-title" className="space-y-6">
      <div>
        <h1 className="text-3xl font-medium tracking-tight" id="school-documents-title">
          {t("school.documents.title")}
        </h1>
        <p className="mt-2 text-muted-foreground">{t("school.documents.description")}</p>
      </div>
      <SchoolSetupDocumentField
        controlId="school.documents.coursePlan"
        label={t("school.documents.coursePlan")}
        onChange={(coursePlan) => patch({ coursePlan })}
        value={profile.coursePlan}
      />
      <SchoolSetupDocumentField
        controlId="school.documents.trainingPlan"
        label={t("school.documents.trainingPlan")}
        onChange={(trainingPlan) => patch({ trainingPlan })}
        value={profile.trainingPlan}
      />
      <SchoolSetupDocumentField
        controlId="school.documents.learningPaths"
        label={t("school.documents.learningPaths")}
        onChange={(learningPaths) => patch({ learningPaths })}
        value={profile.learningPaths}
      />
    </section>
  );
}
